// src/pages/platform/PlatformAuthProvidersPage.tsx
// Platform admin page for managing OAuth2 identity providers.

import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useAuthn, usePlatform, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Alert,
  Badge,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';
import type { OAuth2Provider } from '@core/types/authn';
import {
  EMPTY_PROVIDER_FORM,
  PROVIDER_TYPES,
  validateProviderForm,
  scopesStringToList,
  scopesListToString,
} from '../../features/platform/auth-providers';
import type { ProviderFormState } from '../../features/platform/auth-providers';

type ViewMode = 'list' | 'create' | 'edit';

export const PlatformAuthProvidersPage = observer(() => {
  const { id: platformId } = useParams<{ id: string }>();
  const authnStore = useAuthn();
  const platformStore = usePlatform();
  const { t } = useI18n();

  const [providers, setProviders] = useState<OAuth2Provider[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ProviderFormState>(EMPTY_PROVIDER_FORM);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<unknown>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const platformName = (platformStore.currentItem as any)?.name || t('platform.title');

  useEffect(() => {
    if (platformId) {
      platformStore.GetPlatform(platformId);
    }
    loadProviders();
  }, [platformId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProviders = async () => {
    setLoading(true);
    try {
      const resp = await authnStore.ListOAuth2Providers();
      setProviders(resp.providers?.items ?? []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = <K extends keyof ProviderFormState>(key: K, value: ProviderFormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (formErrors[key]) setFormErrors(prev => ({ ...prev, [key]: '' }));
  };

  const handleCreate = () => {
    setForm(EMPTY_PROVIDER_FORM);
    setFormErrors({});
    setViewMode('create');
    setError(null);
    setSuccess(null);
  };

  const handleEdit = (provider: OAuth2Provider) => {
    setForm({
      provider_name: provider.provider_name,
      provider_type: provider.provider_type,
      description: provider.description,
      icon_url: provider.icon_url,
      client_id: provider.client_id,
      client_secret: '',
      authorization_url: provider.authorization_url,
      token_url: provider.token_url,
      userinfo_url: provider.userinfo_url,
      scopes: scopesListToString(provider.scopes?.items),
      enabled: provider.enabled,
      priority: provider.priority,
    });
    setEditingId(provider.entity?.id ?? null);
    setFormErrors({});
    setViewMode('edit');
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setViewMode('list');
    setEditingId(null);
    setFormErrors({});
  };

  const handleSubmit = async () => {
    const validation = validateProviderForm(form);
    if (Object.keys(validation).length > 0) {
      setFormErrors(Object.fromEntries(Object.entries(validation).map(([k, v]) => [k, t(v)])));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      if (viewMode === 'create') {
        await authnStore.CreateOAuth2Provider({
          entity: {} as never,
          provider_name: form.provider_name,
          provider_type: form.provider_type,
          description: form.description,
          icon_url: form.icon_url,
          client_id: form.client_id,
          client_secret: form.client_secret,
          authorization_url: form.authorization_url,
          token_url: form.token_url,
          userinfo_url: form.userinfo_url,
          scopes: { items: scopesStringToList(form.scopes) },
        });
        setSuccess(t('platform.authProviders.created'));
      } else if (editingId) {
        await authnStore.UpdateOAuth2Provider(editingId, {
          entity: {} as never,
          provider_name: form.provider_name,
          description: form.description,
          icon_url: form.icon_url,
          client_id: form.client_id,
          client_secret: form.client_secret,
          authorization_url: form.authorization_url,
          token_url: form.token_url,
          userinfo_url: form.userinfo_url,
          scopes: { items: scopesStringToList(form.scopes) },
          enabled: form.enabled,
          priority: form.priority,
        });
        setSuccess(t('platform.authProviders.updated'));
      }

      setViewMode('list');
      setEditingId(null);
      await loadProviders();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleEnabled = async (provider: OAuth2Provider) => {
    try {
      await authnStore.UpdateOAuth2Provider(provider.entity?.id ?? '', {
        entity: {} as never,
        provider_name: provider.provider_name,
        description: provider.description,
        icon_url: provider.icon_url,
        client_id: provider.client_id,
        client_secret: '',
        authorization_url: provider.authorization_url,
        token_url: provider.token_url,
        userinfo_url: provider.userinfo_url,
        scopes: provider.scopes,
        enabled: !provider.enabled,
        priority: provider.priority,
      });
      await loadProviders();
    } catch (err) {
      setError(err);
    }
  };

  const handleDelete = async (providerId: string) => {
    try {
      await authnStore.DeleteOAuth2Provider(providerId);
      setDeleteConfirmId(null);
      setSuccess(t('platform.authProviders.deleted'));
      await loadProviders();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err);
    }
  };

  // ---------------------------------------------------------------------------
  // Provider Form
  // ---------------------------------------------------------------------------

  const renderForm = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>
            {viewMode === 'create' ? t('platform.authProviders.addProvider') : t('platform.authProviders.editProvider')}
          </CardTitle>
          <CardDescription>{t('platform.authProviders.formDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error ? <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} /> : null}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label={t('platform.authProviders.providerName')}
              value={form.provider_name}
              onChange={e => handleFormChange('provider_name', e.target.value)}
              placeholder="Google"
              error={formErrors.provider_name}
            />
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                {t('platform.authProviders.providerType')}
              </label>
              <Select value={form.provider_type} onChange={e => handleFormChange('provider_type', e.target.value)}>
                {PROVIDER_TYPES.map(pt => (
                  <option key={pt.value} value={pt.value}>{t(pt.labelKey)}</option>
                ))}
              </Select>
            </div>
          </div>

          <Input
            label={t('platform.authProviders.description')}
            value={form.description}
            onChange={e => handleFormChange('description', e.target.value)}
            placeholder={t('platform.authProviders.descriptionPlaceholder')}
          />

          <div>
            <Input
              label={t('platform.authProviders.iconUrl')}
              value={form.icon_url}
              onChange={e => handleFormChange('icon_url', e.target.value)}
              placeholder="https://example.com/icon.svg"
            />
            {form.icon_url && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{t('platform.authProviders.iconPreview')}:</span>
                <img src={form.icon_url} alt="" className="h-6 w-6 object-contain rounded" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label={t('platform.authProviders.clientId')}
              value={form.client_id}
              onChange={e => handleFormChange('client_id', e.target.value)}
              error={formErrors.client_id}
            />
            <Input
              label={t('platform.authProviders.clientSecret')}
              type="password"
              value={form.client_secret}
              onChange={e => handleFormChange('client_secret', e.target.value)}
              placeholder={viewMode === 'edit' ? t('platform.authProviders.leaveEmptyToKeep') : ''}
            />
          </div>

          <Input
            label={t('platform.authProviders.authorizationUrl')}
            value={form.authorization_url}
            onChange={e => handleFormChange('authorization_url', e.target.value)}
            placeholder="https://accounts.google.com/o/oauth2/auth"
            error={formErrors.authorization_url}
          />
          <Input
            label={t('platform.authProviders.tokenUrl')}
            value={form.token_url}
            onChange={e => handleFormChange('token_url', e.target.value)}
            placeholder="https://oauth2.googleapis.com/token"
            error={formErrors.token_url}
          />
          <Input
            label={t('platform.authProviders.userinfoUrl')}
            value={form.userinfo_url}
            onChange={e => handleFormChange('userinfo_url', e.target.value)}
            placeholder="https://www.googleapis.com/oauth2/v3/userinfo"
            error={formErrors.userinfo_url}
          />
          <Input
            label={t('platform.authProviders.scopes')}
            value={form.scopes}
            onChange={e => handleFormChange('scopes', e.target.value)}
            placeholder="openid, email, profile"
          />
          <p className="text-xs text-muted-foreground -mt-2">{t('platform.authProviders.scopesHint')}</p>

          {viewMode === 'edit' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 py-2">
                <button
                  type="button"
                  onClick={() => handleFormChange('enabled', !form.enabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.enabled ? 'bg-primary' : 'bg-muted'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
                <span className="text-sm font-medium text-foreground">{t('platform.authProviders.enabled')}</span>
              </div>
              <Input
                label={t('platform.authProviders.priority')}
                type="number"
                value={String(form.priority)}
                onChange={e => handleFormChange('priority', parseInt(e.target.value, 10) || 0)}
              />
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-end gap-2">
          <Button variant="secondary" onClick={handleCancel}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? t('common.saving') : t('common.save')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Provider List
  // ---------------------------------------------------------------------------

  const renderList = () => (
    <div className="space-y-4">
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <span className="text-muted-foreground">{t('common.loading')}</span>
        </div>
      ) : providers.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground mb-4">{t('platform.authProviders.noProviders')}</p>
            <Button onClick={handleCreate}>{t('platform.authProviders.addProvider')}</Button>
          </CardContent>
        </Card>
      ) : (
        providers
          .sort((a, b) => a.priority - b.priority)
          .map(provider => (
            <Card key={provider.entity?.id}>
              <CardContent className="py-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  {/* Provider info */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {provider.icon_url ? (
                      <img src={provider.icon_url} alt="" className="h-10 w-10 object-contain rounded shrink-0" />
                    ) : (
                      <div className="h-10 w-10 rounded bg-muted flex items-center justify-center shrink-0">
                        <svg className="h-5 w-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.86-2.813a4.5 4.5 0 00-6.364-6.364L4.5 8.25l4.5 4.5" />
                        </svg>
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{provider.provider_name}</p>
                      <p className="text-xs text-muted-foreground">{provider.provider_type} · {t('platform.authProviders.priority')} {provider.priority}</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <Badge variant={provider.enabled ? 'success' : 'warning'}>
                      {provider.enabled ? t('platform.authProviders.active') : t('platform.authProviders.inactive')}
                    </Badge>

                    <button
                      type="button"
                      onClick={() => handleToggleEnabled(provider)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${provider.enabled ? 'bg-primary' : 'bg-muted'}`}
                      aria-label={t('platform.authProviders.toggleEnabled')}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${provider.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>

                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(provider)}>
                        {t('common.edit')}
                      </Button>

                      {deleteConfirmId === provider.entity?.id ? (
                        <>
                          <Button variant="danger" size="sm" onClick={() => handleDelete(provider.entity?.id ?? '')}>
                            {t('common.confirm')}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmId(null)}>
                            {t('common.cancel')}
                          </Button>
                        </>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmId(provider.entity?.id ?? null)}>
                          {t('common.delete')}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
      )}
    </div>
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div>
        <nav className="text-sm mb-2">
          <Link to="/platforms" className="text-muted-foreground hover:text-foreground">{t('platform.list')}</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to={`/platforms/${platformId}`} className="text-muted-foreground hover:text-foreground">{platformName}</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('platform.authProviders.title')}</span>
        </nav>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t('platform.authProviders.title')}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t('platform.authProviders.subtitle')}</p>
          </div>
          {viewMode === 'list' && providers.length > 0 && (
            <Button onClick={handleCreate}>{t('platform.authProviders.addProvider')}</Button>
          )}
        </div>
      </div>

      {/* Alerts */}
      {success && <Alert variant="success" dismissible onDismiss={() => setSuccess(null)}>{success}</Alert>}
      {error && viewMode === 'list' ? <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} /> : null}

      {/* Content */}
      {viewMode === 'list' ? renderList() : renderForm()}
    </div>
  );
});

export default PlatformAuthProvidersPage;
